import { SelectionModel } from '@angular/cdk/collections';
import { Component, computed, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Data2Service } from '../../common/data2.service';
import { DateService } from '../../common/date.service';
import { InfoboxService } from '../../common/infobox/infobox.service';
import { BankTransactionsFiltersComponent } from './bank-transactions-filters/bank-transactions-filters.component';
import { DataBagService } from 'src/app/common/data-bag.service';
import { LoaderService } from 'src/app/common/loader/loader.service';
import { Vehicle } from '@/vehicles/vehicle.model';
import { Users } from '@/accommodations/accommodations.model';
import { Driver } from '@/drivers/driver.model';
import {
	ActionKind,
	BankCardsHistory,
	BankCardsHistoryParameters,
	BankDepartments,
	BankFilters,
	BankOwnersTypes,
	BankOwnersTypesOther,
	BankSummaryValue,
	CurrentOwnerName,
	TransactionKind,
} from '../bank.model';
import { MatDialog } from '@angular/material/dialog';
import { BankTransactionsBulkUpdateDialogComponent } from './bank-transactions-bulk/bank-transactions-bulk-update-dialog.component';
import { filter } from 'rxjs';

const FLIX_TRANSFERS_FILTER = 'transfersOnly=1&flixOnly=1';

@Component({
	selector: 'app-bank-transactions',
	templateUrl: './bank-transactions.component.html',
	styleUrls: ['./bank-transactions.component.css'],
})
export class BankTransactionsComponent implements OnInit {
	selection = new SelectionModel<BankCardsHistory>(true, []);
	loader = true;
	data: BankCardsHistory[] = [];
	summaryValue: BankSummaryValue = {
		all: 0,
		out: 0,
	};
	objTypes: BankOwnersTypes[] = [];
	users: Users[] = [];
	drivers: Driver[] = [];
	vehicles: Vehicle[] = [];
	departments: BankDepartments[] = [];
	ownersOther: BankOwnersTypesOther[] = [];
	countries: Location[] = [];
	types: TransactionKind[] = [];
	currentOwnerName: CurrentOwnerName[] = [];
	sortKey: string;
	filters: BankFilters;
	parameters: BankCardsHistoryParameters = {
		currentOwnerName: [],
	};

	readonly currentUrl = signal(this.router.url);

	readonly isBankTransactionsTransfers = computed(() => this.currentUrl().includes('bankTransactions/transfers'));

	protected readonly ActionKind = ActionKind;
	private itemsBeforeDataLoad = 3;
	private sortAscending = false;
	private scrollToId = '';

	constructor(
		protected router: Router,
		private readonly api: Data2Service,
		private readonly bag: DataBagService,
		private readonly infobox: InfoboxService,
		private readonly date: DateService,
		private readonly route: ActivatedRoute,
		private readonly loaderServ: LoaderService,
		private readonly matDialog: MatDialog,
	) {
		this.route.fragment.subscribe((fragment: string) => {
			this.scrollToId = fragment;
		});
	}

	ngOnInit(): void {
		this.api.get('/bankOwnersTypes/', (result, error) => {
			this.objTypes = result.data;
		});
		this.api.get('/bankCardsOwnersList', (result, error) => {
			this.parameters.currentOwnerName = result.data;
		});
		this.bag.get('/location/basic/?limit=99999&location_kind=CR', (result, error) => {
			result.results?.forEach((obj) => (obj.id = obj.id.toString()));
			this.countries = result.results;
			this.loadData();
		});
		this.api.get('/bankTransactionsTypes/', (result, error) => {
			this.types = result.data;
			this.loadData();
		});
		this.scrollToLastVisited();
	}

	loadData(): void {
		this.itemsBeforeDataLoad--;
		if (this.itemsBeforeDataLoad > 0) return;
		this.loader = true;
		let parms = this.api.getChecked(this.filters, this.parameters);
		this.summaryValue.out = 0;
		this.summaryValue.all = 0;

		if (this.isBankTransactionsTransfers()) {
			const separator = !!parms.length ? '&' : '?';
			parms += `${separator}${FLIX_TRANSFERS_FILTER}`;
		}

		this.api.get(`/bankTransactions/?${parms}`, (result, error) => {
			result.data.forEach((element) => {
				this.summaryValue.all += parseFloat(element.countValue);
				element.scrollToId = element.id;
				element.verified = element.verified == '1';
				element.calculated = element.calculated == '1';
				element.originalDocument = element.originalDocument == '1';
				element.ownerName =
					element.currentOwnerName != '?' ? element.currentOwnerName : '? ' + element.cardNumber;
				switch (element.operation) {
					case 'in':
						element.value = '+' + element.value;
						break;
					case 'out':
						this.summaryValue.out += parseFloat(element.countValue);
						break;
				}
				element.fv_number = element.fvNumber;
				element.class = `operation-${element.operation}`;
				if (this.scrollToId && this.scrollToId == element.id) {
					element.lastVisited = 'lastEdited';
				}
			});
			this.data = result.data;
			this.sortLoad();
			this.loader = false;
			this.scrollToLastVisited();
		});
	}

	scrollToLastVisited(): void {
		if (!this.scrollToId) return;
		setTimeout(() => {
			const lastEditedElement = document.getElementById(this.scrollToId);
			if (!lastEditedElement) {
				return;
			}
			window.scrollTo({
				top: lastEditedElement.getBoundingClientRect().top - 70,
				behavior: 'smooth',
			});
		});
	}

	editFvNumber(item: BankCardsHistory): boolean {
		const editable = this.data.filter((obj) => obj.id === item.id && obj.fvNumber !== item.fv_number);
		if (!editable.length) {
			return (item.edited = false);
		}
		return (item.edited = true);
	}

	setAction(item: BankCardsHistory, action: ActionKind): void {
		this.loaderServ.show();
		if (action !== 'originalDocument') item[action] = !item[action];
		this.api.command(
			`/bankTransactions/${item.positionId}/`,
			{ cmd: { action: 'command', field: action }, set: item[action] },
			(result, error) => {
				if (error) {
					if (action !== 'originalDocument') {
						item[action] = !item[action];
					}
					this.loaderServ.hide();
				}
				this.loaderServ.hide();
			},
		);
	}

	saveFvNumber(item: BankCardsHistory): void {
		this.loaderServ.show();
		this.api.command(
			`/bankTransactions/${item.positionId}/`,
			{
				cmd: { action: 'fvNumber', kind: 'fvNumber' },
				set: item.fv_number,
			},
			(result, error) => {
				if (error) {
					return this.loaderServ.hide();
				}
				this.loaderServ.hide();
				item.edited = false;
			},
		);
	}

	setType(item: BankCardsHistory): void {
		if (!item.type) item.type = null;
		this.api.command(
			`/bankTransactions/${item.positionId}/`,
			{ cmd: { action: 'setType', kind: 'type' }, set: item.type },
			(result, error) => {
				if (error) {
					return;
				}
			},
		);
	}

	setCountry(item: BankCardsHistory): void {
		if (!item.country) item.country = null;
		this.api.command(
			`/bankTransactions/${item.positionId}/`,
			{
				cmd: { action: 'setCountry', kind: 'country' },
				set: item.country,
			},
			(result, error) => {
				if (error) {
					this.infobox.error('Błąd podczas zapisu - odśwież stronę');
					return;
				}
			},
		);
	}

	setOwnerTransaction(item: BankCardsHistory, event: string): string {
		this.api.command(
			`/bankTransactions/${item.positionId}/`,
			{
				cmd: { action: 'setOwnerTransaction', kind: 'ownerTransaction' },
				set: event,
			},
			(result, error) => {
				if (error) {
					this.infobox.error('Błąd podczas zapisu - odśwież stronę');
					return;
				}
			},
		);
		return (item.currentOwnerTransactions = event);
	}

	filtersOpen(): void {
		new BankTransactionsFiltersComponent(this.api, this.route, this.router).filtersOpen();
	}

	filtersSelected(data: BankFilters): void {
		if (!data.dateFrom) data.dateFrom = this.date.now().date;
		if (!data.dateTo) data.dateTo = this.date.now().date;
		this.filters = data;
		this.loadData();
	}

	setSort(key: string): void {
		this.sortKey = key;
		this.sortAscending = !this.sortAscending;
		this.sortSave();
		this.sortSort();
	}

	async displayScan(scanId: string): Promise<void> {
		window.open(`${window.origin}/bankTransactions/scan/${scanId}`, '_blank', 'width=1200, height=800');
	}

	private sortSort(): void {
		if (this.sortAscending) {
			this.data = this.data.sort((a, b) => a[this.sortKey]?.localeCompare(b[this.sortKey]));
		} else {
			this.data = this.data.sort((a, b) => b[this.sortKey]?.localeCompare(a[this.sortKey]));
		}
	}

	private sortSave(): void {
		localStorage.setItem('bankTransactions-sortKey', JSON.stringify(this.sortKey));
		localStorage.setItem('bankTransactions-sortAscending', JSON.stringify(this.sortAscending));
	}

	private sortLoad(): void {
		if (localStorage['bankTransactions-sortKey']) {
			this.sortKey = JSON.parse(localStorage['bankTransactions-sortKey']);
		}
		if (localStorage['bankTransactions-sortAscending']) {
			this.sortAscending = JSON.parse(localStorage['bankTransactions-sortAscending']);
		}
		this.sortSort();
	}

	isAllSelected(): boolean {
		const numSelected = this.selection.selected.length;
		const numRows = this.data.length;
		return numSelected === numRows;
	}

	masterToggle(): void {
		if (this.isAllSelected()) {
			this.selection.clear();
		} else {
			this.selection.select(...this.data.filter((row) => !row.verified));
		}
	}

	anySelected(): boolean {
		return this.selection.selected.length > 0;
	}

	openBulkUpdateDialog(): void {
		const dialogRef = this.matDialog.open(BankTransactionsBulkUpdateDialogComponent, {
			width: '500px',
			data: {
				selected: this.selection.selected,
				countries: this.countries,
				types: this.types,
				currentOwnerTransactions: this.parameters.currentOwnerName,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe((result) => {
				this.loadData();
				this.selection.clear();
			});
	}
}
